import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumBeerflexOrderConfirmationStatus } from "./enums/enumBeerflexOrderConfirmationStatus.generated";
import { EnumBeerflexOrderType } from "./enums/enumBeerflexOrderType.generated";

// Model Objects

export class BeerflexOrderHeader extends ModelObject<BeerflexOrderHeader> {

    public id: Guid = Guid.createEmpty();
    public beerflexOrderType: EnumBeerflexOrderType = EnumBeerflexOrderType.none;
    /**
    * The Beerflex order number of this order.
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public beerflexOrderNumber: string = "";
    /**
    * The date the order was placed on the Beerflex system.
    */
    public createdDate: LocalDate = LocalDate.ofEpochDay(0);
    /**
    * The Beerflex brewery code against which the order was made.
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public breweryCode: string = "";
    /**
    * The Beerflex Customer Number. This represents the Chain placing the order - more like a BrewMan Group than a BrewMan Outlet.
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public customerNumber: string = "";
    /**
    * The Beerflex Customer Name. This represents the Chain placing the order - more like a BrewMan Group than a BrewMan Outlet.
    * Not Nullable
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public customerName: string = "";
    /**
    * The Beerflex Customer Order Number.
    * Not Nullable
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public customerOrderNumber: string = "";
    public customerOrderRef: string = "";
    /**
    * The Property Number of the Customer. This is equivalent to a BrewMan Outlet.
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public propertyNumber: string = "";
    /**
    * The name of the Licensee at the property.
    * Not Nullable
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public licensee: string = "";
    /**
    * The name of the property, which is used as the DeliveryAddressBusiness.
    * Not Nullable
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public propertyName: string = "";
    /**
    * Line 1 of delivery address.
    * Not Nullable
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public addressLine1: string = "";
    /**
    * Line 2 of delivery address.
    * Not Nullable
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public addressLine2: string = "";
    /**
    * Line 3 of delivery address.
    * Not Nullable
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public addressLine3: string = "";
    /**
    * Line 4 of delivery address.
    * Not Nullable
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public addressLine4: string = "";
    /**
    * Delivery postcode.
    * Not Nullable
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public postcode: string = "";
    /**
    * Delivery Telephone number.
    * Not Nullable
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public telephoneNumber: string = "";
    /**
    * Required By Date.
    */
    public requiredDeliveryDate: LocalDate = LocalDate.ofEpochDay(0);
    public deliveryInstructions: string = "";
    public beerflexDeliveryNoteNumber: string = "";
    /**
    * The total number of items in the order. (Beerflex quantities are integers).
    */
    public totalQuantity: number = 0;
    private _totalNetPrice: number | null = null;
    /**
    * The total net value of the order including delivery.
    */
    public get totalNetPrice(): number | null { return this._totalNetPrice }
    public set totalNetPrice(value: number | null) {
      this._totalNetPrice = value === undefined ? null : value;
    }
    private _brewmanOutletId: Guid | null = null;
    /**
    * The Brewman outlet id mapped to this order during the last import operation. No value here would indicate a missing outlet mapping.
    */
    public get brewmanOutletId(): Guid | null { return this._brewmanOutletId }
    public set brewmanOutletId(value: Guid | null) {
      this._brewmanOutletId = value === undefined ? null : value;
    }
    private _brewmanOrderId: Guid | null = null;
    /**
    * The Brewman order id assigned to this order on a successful import. If empty, check the error message field for details.
    */
    public get brewmanOrderId(): Guid | null { return this._brewmanOrderId }
    public set brewmanOrderId(value: Guid | null) {
      this._brewmanOrderId = value === undefined ? null : value;
    }
    /**
    * Error information associated with a failed import.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public errorMessage: string = "";
    private _importErroredAt: Instant | null = null;
    /**
    * The timestamp order import failed due to an error. Failed orders can be retried slowly as the error could have been fixed within BrewMan or Sellar.
    */
    public get importErroredAt(): Instant | null { return this._importErroredAt }
    public set importErroredAt(value: Instant | null) {
      this._importErroredAt = value === undefined ? null : value;
    }
    public confirmationStatus: EnumBeerflexOrderConfirmationStatus = EnumBeerflexOrderConfirmationStatus.none;
    private _importedAt: Instant | null = null;
    /**
    * The timestamp order was successfully imported to Brewman.
    */
    public get importedAt(): Instant | null { return this._importedAt }
    public set importedAt(value: Instant | null) {
      this._importedAt = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.beerflexOrderType = EnumBeerflexOrderType.getByValue(jsonSerializedObject.beerflexOrderType, this.beerflexOrderType);
          this.beerflexOrderNumber = jsonSerializedObject.beerflexOrderNumber;
          this.createdDate = LocalDate.parse(jsonSerializedObject.createdDate);
          this.breweryCode = jsonSerializedObject.breweryCode;
          this.customerNumber = jsonSerializedObject.customerNumber;
          this.customerName = jsonSerializedObject.customerName;
          this.customerOrderNumber = jsonSerializedObject.customerOrderNumber;
          this.customerOrderRef = jsonSerializedObject.customerOrderRef;
          this.propertyNumber = jsonSerializedObject.propertyNumber;
          this.licensee = jsonSerializedObject.licensee;
          this.propertyName = jsonSerializedObject.propertyName;
          this.addressLine1 = jsonSerializedObject.addressLine1;
          this.addressLine2 = jsonSerializedObject.addressLine2;
          this.addressLine3 = jsonSerializedObject.addressLine3;
          this.addressLine4 = jsonSerializedObject.addressLine4;
          this.postcode = jsonSerializedObject.postcode;
          this.telephoneNumber = jsonSerializedObject.telephoneNumber;
          this.requiredDeliveryDate = LocalDate.parse(jsonSerializedObject.requiredDeliveryDate);
          this.deliveryInstructions = jsonSerializedObject.deliveryInstructions;
          this.beerflexDeliveryNoteNumber = jsonSerializedObject.beerflexDeliveryNoteNumber;
          this.totalQuantity = jsonSerializedObject.totalQuantity;
          this.totalNetPrice = jsonSerializedObject.totalNetPrice == null ? null : jsonSerializedObject.totalNetPrice;
          this.brewmanOutletId = jsonSerializedObject.brewmanOutletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.brewmanOutletId);
          this.brewmanOrderId = jsonSerializedObject.brewmanOrderId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.brewmanOrderId);
          this.errorMessage = jsonSerializedObject.errorMessage;
          this.importErroredAt = jsonSerializedObject.importErroredAt == null ? null : Instant.parse(jsonSerializedObject.importErroredAt);
          this.confirmationStatus = EnumBeerflexOrderConfirmationStatus.getByValue(jsonSerializedObject.confirmationStatus, this.confirmationStatus);
          this.importedAt = jsonSerializedObject.importedAt == null ? null : Instant.parse(jsonSerializedObject.importedAt);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.beerflexOrderType = this.beerflexOrderType.value;
        toRet.beerflexOrderNumber = this.beerflexOrderNumber;
        toRet.createdDate = this.createdDate.toString();
        toRet.breweryCode = this.breweryCode;
        toRet.customerNumber = this.customerNumber;
        toRet.customerName = this.customerName;
        toRet.customerOrderNumber = this.customerOrderNumber;
        toRet.customerOrderRef = this.customerOrderRef;
        toRet.propertyNumber = this.propertyNumber;
        toRet.licensee = this.licensee;
        toRet.propertyName = this.propertyName;
        toRet.addressLine1 = this.addressLine1;
        toRet.addressLine2 = this.addressLine2;
        toRet.addressLine3 = this.addressLine3;
        toRet.addressLine4 = this.addressLine4;
        toRet.postcode = this.postcode;
        toRet.telephoneNumber = this.telephoneNumber;
        toRet.requiredDeliveryDate = this.requiredDeliveryDate.toString();
        toRet.deliveryInstructions = this.deliveryInstructions;
        toRet.beerflexDeliveryNoteNumber = this.beerflexDeliveryNoteNumber;
        toRet.totalQuantity = this.totalQuantity;
        toRet.totalNetPrice = this.totalNetPrice == null ? null : this.totalNetPrice;
        toRet.brewmanOutletId = this.brewmanOutletId == null ? null : this.brewmanOutletId.toString();
        toRet.brewmanOrderId = this.brewmanOrderId == null ? null : this.brewmanOrderId.toString();
        toRet.errorMessage = this.errorMessage;
        toRet.importErroredAt = this.importErroredAt == null ? null : this.importErroredAt.toString();
        toRet.confirmationStatus = this.confirmationStatus.value;
        toRet.importedAt = this.importedAt == null ? null : this.importedAt.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "BeerflexOrderHeader.Id cannot be empty"},
            {field: "beerflexOrderNumber", rule: (v: any) => v == null || v.length !== 0 || "BeerflexOrderNumber cannot be empty"},
            {field: "beerflexOrderNumber", rule: (v: any) => v == null || v.length <= 1000 || "BeerflexOrderNumber has too many characters (1000)"},
            {field: "breweryCode", rule: (v: any) => v == null || v.length !== 0 || "BreweryCode cannot be empty"},
            {field: "breweryCode", rule: (v: any) => v == null || v.length <= 1000 || "BreweryCode has too many characters (1000)"},
            {field: "customerNumber", rule: (v: any) => v == null || v.length !== 0 || "CustomerNumber cannot be empty"},
            {field: "customerNumber", rule: (v: any) => v == null || v.length <= 1000 || "CustomerNumber has too many characters (1000)"},
            {field: "customerName", rule: (v: any) => v == null || v.length <= 1000 || "CustomerName has too many characters (1000)"},
            {field: "customerOrderNumber", rule: (v: any) => v == null || v.length <= 1000 || "CustomerOrderNumber has too many characters (1000)"},
            {field: "customerOrderRef", rule: (v: any) => v == null || v.length <= 1000 || "CustomerOrderRef has too many characters (1000)"},
            {field: "propertyNumber", rule: (v: any) => v == null || v.length !== 0 || "PropertyNumber cannot be empty"},
            {field: "propertyNumber", rule: (v: any) => v == null || v.length <= 1000 || "PropertyNumber has too many characters (1000)"},
            {field: "licensee", rule: (v: any) => v == null || v.length <= 1000 || "Licensee has too many characters (1000)"},
            {field: "propertyName", rule: (v: any) => v == null || v.length <= 1000 || "PropertyName has too many characters (1000)"},
            {field: "addressLine1", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine1 has too many characters (1000)"},
            {field: "addressLine2", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine2 has too many characters (1000)"},
            {field: "addressLine3", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine3 has too many characters (1000)"},
            {field: "addressLine4", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine4 has too many characters (1000)"},
            {field: "postcode", rule: (v: any) => v == null || v.length <= 1000 || "Postcode has too many characters (1000)"},
            {field: "telephoneNumber", rule: (v: any) => v == null || v.length <= 1000 || "TelephoneNumber has too many characters (1000)"},
            {field: "deliveryInstructions", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryInstructions has too many characters (1000)"},
            {field: "beerflexDeliveryNoteNumber", rule: (v: any) => v == null || v.length <= 1000 || "BeerflexDeliveryNoteNumber has too many characters (1000)"},
            {field: "totalQuantity", rule: (v: any) => v == null || v >= -2147483648 || "TotalQuantity is not greater than minimum allowed value: -2147483648"},
            {field: "totalQuantity", rule: (v: any) => v == null || v <= 2147483647 || "TotalQuantity is above maximum allowed value: 2147483647"},
            {field: "brewmanOutletId", rule: (v: any) => v == null || !v.isEmpty() || "BeerflexOrderHeader.BrewmanOutletId cannot be empty"},
            {field: "brewmanOrderId", rule: (v: any) => v == null || !v.isEmpty() || "BeerflexOrderHeader.BrewmanOrderId cannot be empty"},
    ];
}


export class BeerflexOrderLine extends ModelObject<BeerflexOrderLine> {

    public id: Guid = Guid.createEmpty();
    /**
    * The order to which this line belongs.
    */
    public beerflexOrderId: Guid = Guid.createEmpty();
    /**
    * The Description of the product as it appears on Beerflex system.
    * Not Nullable
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public productDescription: string = "";
    /**
    * The Code of the product on Beerflex system.
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public beerflexProductCode: string = "";
    /**
    * The date the order was placed on the Beerflex system.
    */
    public quantity: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.beerflexOrderId = Guid.parseOrEmpty(jsonSerializedObject.beerflexOrderId);
          this.productDescription = jsonSerializedObject.productDescription;
          this.beerflexProductCode = jsonSerializedObject.beerflexProductCode;
          this.quantity = jsonSerializedObject.quantity;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.beerflexOrderId = this.beerflexOrderId.toString();
        toRet.productDescription = this.productDescription;
        toRet.beerflexProductCode = this.beerflexProductCode;
        toRet.quantity = this.quantity;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "BeerflexOrderLine.Id cannot be empty"},
            {field: "beerflexOrderId", rule: (v: any) => !v.isEmpty() || "BeerflexOrderLine.BeerflexOrderId cannot be empty"},
            {field: "productDescription", rule: (v: any) => v == null || v.length <= 1000 || "ProductDescription has too many characters (1000)"},
            {field: "beerflexProductCode", rule: (v: any) => v == null || v.length !== 0 || "BeerflexProductCode cannot be empty"},
            {field: "beerflexProductCode", rule: (v: any) => v == null || v.length <= 1000 || "BeerflexProductCode has too many characters (1000)"},
            {field: "quantity", rule: (v: any) => v == null || v >= -2147483648 || "Quantity is not greater than minimum allowed value: -2147483648"},
            {field: "quantity", rule: (v: any) => v == null || v <= 2147483647 || "Quantity is above maximum allowed value: 2147483647"},
    ];
}


export class BeerflexOrder extends ModelObject<BeerflexOrder> {

    public header: BeerflexOrderHeader = new BeerflexOrderHeader();
    public lines: (BeerflexOrderLine)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new BeerflexOrderHeader(jsonSerializedObject.header);
          this.lines = jsonSerializedObject.lines.map((v: any) => new BeerflexOrderLine(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.lines = this.lines.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "lines", rule: (v: any) => !!v || "lines is required"},
            {field: "lines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as BeerflexOrderLine[]).reduce((acc: Array<string>, curr: BeerflexOrderLine) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class FilteredBeerflexOrdersResult extends ModelObject<FilteredBeerflexOrdersResult> {

    public matchingCount: number = 0;
    public orders: (BeerflexOrder)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.orders = jsonSerializedObject.orders.map((v: any) => new BeerflexOrder(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.orders = this.orders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "orders", rule: (v: any) => !!v || "orders is required"},
            {field: "orders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as BeerflexOrder[]).reduce((acc: Array<string>, curr: BeerflexOrder) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class BeerflexOrderFilter extends ModelObject<BeerflexOrderFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _createdFromDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) created at date
    */
    public get createdFromDate(): LocalDate | null { return this._createdFromDate }
    public set createdFromDate(value: LocalDate | null) {
      this._createdFromDate = value === undefined ? null : value;
    }
    private _createdToDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) created at date
    */
    public get createdToDate(): LocalDate | null { return this._createdToDate }
    public set createdToDate(value: LocalDate | null) {
      this._createdToDate = value === undefined ? null : value;
    }
    private _beerflexOrderNumbers: readonly (string)[] | null = null;
    /**
    * Limit to specified beerflex order numbers
    */
    public get beerflexOrderNumbers(): readonly (string)[] | null { return this._beerflexOrderNumbers }
    public set beerflexOrderNumbers(value: readonly (string)[] | null) {
      this._beerflexOrderNumbers = value === undefined ? null : value;
    }
    private _brewmanOrderIds: readonly (Guid)[] | null = null;
    /**
    * Limit to specified Brewman Order Ids
    */
    public get brewmanOrderIds(): readonly (Guid)[] | null { return this._brewmanOrderIds }
    public set brewmanOrderIds(value: readonly (Guid)[] | null) {
      this._brewmanOrderIds = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.createdFromDate = jsonSerializedObject.createdFromDate == null ? null : LocalDate.parse(jsonSerializedObject.createdFromDate);
          this.createdToDate = jsonSerializedObject.createdToDate == null ? null : LocalDate.parse(jsonSerializedObject.createdToDate);
          this.beerflexOrderNumbers = jsonSerializedObject.beerflexOrderNumbers == null ? null : jsonSerializedObject.beerflexOrderNumbers.map((v: any) => v);
          this.brewmanOrderIds = jsonSerializedObject.brewmanOrderIds == null ? null : jsonSerializedObject.brewmanOrderIds.map((v: any) => Guid.parseOrEmpty(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.createdFromDate = this.createdFromDate == null ? null : this.createdFromDate.toString();
        toRet.createdToDate = this.createdToDate == null ? null : this.createdToDate.toString();
        toRet.beerflexOrderNumbers = this.beerflexOrderNumbers == null ? null : this.beerflexOrderNumbers.map(v => v);
        toRet.brewmanOrderIds = this.brewmanOrderIds == null ? null : this.brewmanOrderIds.map(v => v.toString());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class BeerflexOrderService extends ServiceBase {

  public async getBeerflexOrdersByFilter(filter: BeerflexOrderFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredBeerflexOrdersResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredBeerflexOrdersResult(response.results);;
    return this.makeJsonRequest("beerflexOrder/v1/getBeerflexOrdersByFilter", req, false, extractor, opts);
  };
}

export const beerflexOrderService = new BeerflexOrderService();